import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, MessageSquare } from "lucide-react";
import { http } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function useChatSearch(q) {
  const [state, setState] = useState({ busy: false, results: null });
  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setState({ busy: false, results: null }); return undefined; }
    setState((s) => ({ ...s, busy: true }));
    let alive = true;
    const timer = setTimeout(() => http.get("/chats/search", { params: { q: t } })
      .then((r) => alive && setState({ busy: false, results: r.data }))
      .catch(() => alive && setState({ busy: false, results: [] })), 250);
    return () => { alive = false; clearTimeout(timer); };
  }, [q]);
  return state;
}

export function SearchChats({ open, onOpenChange, chats }) {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { busy, results } = useChatSearch(q);
  useEffect(() => { if (!open) setQ(""); }, [open]);
  const go = (id) => { onOpenChange(false); navigate(`/app/c/${id}`); };
  const list = results ?? chats.slice(0, 8);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121725] border-white/10 max-w-xl p-0 gap-0 sm:rounded-2xl top-[12vh] translate-y-0 data-[state=open]:slide-in-from-top-[2%]" data-testid="search-chats">
        <DialogTitle className="sr-only">Search chats</DialogTitle>
        <DialogDescription className="sr-only">Find a chat by its title or by anything said in it.</DialogDescription>
        <div className="flex items-center gap-2 px-4 h-14 border-b border-white/[0.06]">
          {busy ? <Loader2 className="h-4 w-4 text-neutral-500 animate-spin" /> : <Search className="h-4 w-4 text-neutral-500" />}
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chats…" aria-label="Search chats" data-testid="search-chats-input"
            className="flex-1 bg-transparent outline-none text-base placeholder:text-neutral-500 pr-8" />
        </div>
        <div className="max-h-[55vh] overflow-y-auto scroll-thin p-2" data-testid="search-chats-results">
          {!results && <p className="px-2 pt-1 pb-2 text-xs text-neutral-500">{chats.length ? "Recent chats" : "Search your chats by title or by anything said in them."}</p>}
          {results && !results.length && <p className="px-3 py-6 text-sm text-neutral-400 text-center" data-testid="search-chats-empty">No matches for "{q.trim()}"</p>}
          {list.map((c) => (
            <button key={c.chat_id} onClick={() => go(c.chat_id)} data-testid={`search-result-${c.chat_id}`}
              className="w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-white/[0.06] outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70">
              <MessageSquare className="h-4 w-4 text-neutral-500 mt-0.5 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-neutral-100 truncate">{c.title}</span>
                {c.snippet && <span className="block text-xs text-neutral-500 line-clamp-2 mt-0.5">{c.snippet}</span>}
              </span>
              <span className="text-[11px] text-neutral-600 shrink-0">{new Date(c.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
